<template>
  <section class="height-100-p">
    <el-table
      :data="loads.deliverTo.list"
      v-loading="loads.deliverTo.loadingSearch"
      id="deliverToLoads-data-list"
      size="mini"
      tooltip-effect="dark"
      class="dark-blue-table"
      :empty-text="$t('common.notAbleToFindRecords')"
      @sort-change="handleSortChange"
      stripe
    >
      <el-table-column
        prop="origin"
        :label="$t('outbound.loads.origin')"
        :min-width="5"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="shipDate"
        :label="$t('outbound.loads.shipDate')"
        :min-width="5"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.shipDate | moment(dateDisplayFormat)" placement="top">
            <span> {{ scope.row.shipDate | moment(dateDisplayFormat) }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="loadNumber"
        :label="$t('outbound.loads.loadNumber')"
        :min-width="5"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.loadNumber" class="item" effect="dark" :content="scope.row.loadNumber" placement="top">
            <span> {{ scope.row.loadNumber }} </span>
          </el-tooltip>
          <span v-else>
            <receiving-detail-dialog :currentRow="scope.row"/>
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="driver"
        :label="$t('outbound.loads.driver')"
        :min-width="5"
        align="center"
        sortable="custom"
      >
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.driver" placement="top">
            <span>{{ scope.row.driver }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="numberOfUnits"
        :label="$t('outbound.loads.numberOfUnits')"
        :min-width="5"
        align="center"
        sortable="custom">
      </el-table-column>
      <el-table-column
        prop="eta"
        :label="$t('outbound.loads.eta')"
        :min-width="5"
        align="center"
        sortable="custom"
        :show-tooltip-when-overflow="true">
        <template slot-scope="scope">
          <span v-if="scope.row.hideEta">
            <span v-if="!scope.row.delivery && !scope.row.customMessage">
              {{ $t('outbound.loads.hideEta') }}
            </span>
             <span v-else-if="!scope.row.delivery && scope.row.customMessage">
              {{ scope.row.customMessage }}
            </span>
          </span>
          <span v-else>
              <el-tooltip class="item" effect="dark" placement="top">
                  <div slot="content">
                {{ scope.row.tractorLastPosition ? scope.row.tractorLastPosition : "The tractor's last position is not yet available." }}
                    </div>
                <span>
                {{ scope.row.eta | moment(dateTimeDisplayFormat) }}
              </span>
        </el-tooltip>
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="delivery"
        :label="$t('outbound.loads.delivered')"
        :min-width="5"
        align="center"
        sortable="custom">
        <template slot-scope="scope"
        :show-tooltip-when-overflow="true">
          <span>
            {{ scope.row.delivery | moment(dateTimeDisplayFormat) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="invoiceNumber"
        :label="$t('outbound.loads.invoice')"
        :min-width="5"
        align="center"
        sortable="custom">
        <template slot-scope="scope">
          <print-invoice v-if="canPrintInvoice(scope.row)" :id="scope.row.invoiceId" :invoice-type="scope.row.invoiceType" :show-type="'Link'" :link-text="scope.row.invoiceNumber"/>
          <span v-else>{{ scope.row.invoiceNumber }}</span>
        </template>
      </el-table-column>
      <el-table-column
        :min-width="3"
        :label="$t('outbound.loads.ccnBol')"
        align="center"
        header-align="center"
        class-name="fixed-width">
        <template slot-scope="scope">
          <print-ccn-bol v-if="scope.row.loadId > 0 && scope.row.dropId > 0 && scope.row.billOfLadingId > 0" :drop-id="scope.row.dropId" :bol-id="scope.row.billOfLadingId" />
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-footer" v-show="loads.deliverTo.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('outbound.loads.totalLabel') }}</span>
           {{ loads.deliverTo.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="loads.deliverTo.actualFilters.rows"
        :current-page.sync="loads.deliverTo.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="loads.deliverTo.total"
        layout="sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';
import PrintInvoice from '@/views/customerService/components/PrintInvoice';
import { LOADS_DELIVER_TO_SEARCH } from '@/store/actions';
import ReceivingDetailDialog from './components/ReceivingDetailDialog';
import PrintCcnBol from './components/PrintCcnBol';

export default {
  name: 'DeliverToLoadsSummarySection',
  components: { ReceivingDetailDialog, PrintInvoice, PrintCcnBol },
  computed: {
    ...mapGetters(['loads']),
    dateDisplayFormat() {
      return constants.DATES.DEFAULT_DISPLAY_FORMAT;
    },
    dateTimeDisplayFormat() {
      return constants.DATE_TIME.DEFAULT_DISPLAY_FORMAT;
    }
  },
  data() {
    return {
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES
    };
  },
  methods: {
    handleSortChange({ prop, order }) {
      this.$store.dispatch(LOADS_DELIVER_TO_SEARCH, { orderField: prop, orderDirection: constants.TABLES.ORDER_DIRECTION[order] });
    },
    handleSearchChangeLimit(val) {
      this.$store.dispatch(LOADS_DELIVER_TO_SEARCH, { rows: val, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(val) {
      this.$store.dispatch(LOADS_DELIVER_TO_SEARCH, { page: val });
    },
    canPrintInvoice(row) {
      return row.invoiceId > 0 && row.billToAccountId === row.deliverToAccountId
          && (this.$can(this.permissions.MENU_VIEWS.MY_INVOICES_VIEW)
              || this.$can(this.permissions.MENU_VIEWS.INVOICES_VIEW));
    }
  }
};
</script>
